import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Space, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, MessageOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface TaskFeedbackModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (feedback: string) => void;
  taskTitle: string;
  actionType: 'complete' | 'cancel' | 'start_progress';
  loading?: boolean;
}

const TaskFeedbackModal: React.FC<TaskFeedbackModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  taskTitle,
  actionType,
  loading = false
}) => {
  const [form] = Form.useForm();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSubmit(values.feedback);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const getActionInfo = () => {
    if (actionType === 'complete') {
      return {
        title: 'Concluir Tarefa',
        icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px' }} />,
        description: 'Por favor, forneça feedback sobre a conclusão desta tarefa.',
        placeholder: 'Descreva como a tarefa foi concluída, quais foram os resultados, ou qualquer observação relevante...',
        submitText: 'Concluir Tarefa',
        submitType: 'primary' as const
      };
    } else if (actionType === 'start_progress') {
      return {
        title: 'Iniciar Progresso',
        icon: <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '24px' }} />,
        description: 'Por favor, forneça feedback sobre o início do progresso desta tarefa.',
        placeholder: 'Descreva o que planeja fazer, quais são os próximos passos, ou qualquer informação relevante...',
        submitText: 'Iniciar Progresso',
        submitType: 'primary' as const
      };
    } else {
      return {
        title: 'Cancelar Tarefa',
        icon: <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px' }} />,
        description: 'Por favor, explique o motivo do cancelamento desta tarefa.',
        placeholder: 'Descreva o motivo do cancelamento, se houve algum problema, ou qualquer informação relevante...',
        submitText: 'Cancelar Tarefa',
        submitType: 'default' as const
      };
    }
  };

  const actionInfo = getActionInfo();

  return (
    <Modal
      title={
        <Space>
          {actionInfo.icon}
          <span>{actionInfo.title}</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type={actionInfo.submitType}
          icon={
            actionType === 'complete' ? <CheckCircleOutlined /> :
            actionType === 'start_progress' ? <ClockCircleOutlined /> :
            <CloseCircleOutlined />
          }
          onClick={handleSubmit}
          loading={loading}
        >
          {actionInfo.submitText}
        </Button>
      ]}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Tarefa: {taskTitle}
        </Title>
        <Text type="secondary">{actionInfo.description}</Text>
      </div>

      <Alert
        message="Feedback Obrigatório"
        description="Por favor, forneça feedback sobre esta tarefa."
        type="info"
        showIcon
        icon={<MessageOutlined />}
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="feedback"
          label="Feedback"
          rules={[
            {
              required: true,
              message: 'Por favor, forneça feedback sobre esta tarefa.',
            },
            {
              min: 10,
              message: 'O feedback deve ter pelo menos 10 caracteres.',
            },
            {
              max: 1000,
              message: 'O feedback não pode exceder 1000 caracteres.',
            }
          ]}
        >
          <TextArea
            rows={6}
            placeholder={actionInfo.placeholder}
            showCount
            maxLength={1000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TaskFeedbackModal;
